<template>
  <li
    class="pb-mode-item"
    :class="{
      active: props.active,
      'pb-mode-item-any-is-dragged': anyModeIsDragged,
      'pb-mode-item-current-is-dragged': currentModeIsDragged,
      'pb-mode-item-readonly': readonly,
    }"
  >
    <div
      v-if="!isGlobal"
      class="pb-mode-item-index"
    >
      {{ index }}
    </div>
    <!-- Mode Block -->
    <div class="pb-mode-item-content">
      <!-- Dropzone -->
      <div
        class="pb-mode-item-dropzone-wrapper"
      >
        <div
          ref="topDropzone"
          class="pb-mode-item-dropzone pb-mode-item-dropzone-top"
          :class="{ 'pb-mode-item-dropzone-active': topDropzoneActive }"
        />
        <div
          ref="bottomDropzone"
          class="pb-mode-item-dropzone pb-mode-item-dropzone-bottom"
          :class="{ 'pb-mode-item-dropzone-active': bottomDropzoneActive }"
        />
      </div>
      <!-- End Dropzone -->
      <!-- Actual Mode -->
      <LinkBox
        class="pb-mode-item-data"
        :to="href"
        @click.prevent="emit('navigate')"
      >
        <template #before>
          <div
            ref="reorderGrabber"
            class="pb-mode-item-reorder"
            :class="{ 'pb-mode-item-reorder-draggable': dragEnabled }"
          >
            <i class="icon-move-bar" />
          </div>
        </template>

        <div class="pb-mode-item-text-wrapper">
          <div class="pb-mode-item-text">
            {{ mode.shortName }}
            <PbItemDetails>
              {{ itemDetails }}
            </PbItemDetails>
          </div>
          <div class="pb-mode-item-icons-wrapper">
            <ReportIcon
              class="pb-mode-item-report"
              :report="mode.report"
            />
          </div>
        </div>

        <template #after>
          <ButtonWithLoader
            icon="icon-copy"
            class="pb-mode-item-btn-icon"
            :aria-label="duplicateModeTooltip"
            :tooltip="duplicateModeTooltip"
            :disabled="readonly"
            @click="duplicate"
          />
          <ButtonWithLoader
            icon="icon-trash"
            class="pb-mode-item-btn-icon btn-hover-error"
            :aria-label="deleteModeTooltip"
            :tooltip="deleteModeTooltip"
            :disabled="readonly || !deletable"
            @click="openConfirm"
          />
        </template>
      </LinkBox>
      <!-- End Actual Mode -->
    </div>
    <!-- End Mode Block -->
    <!-- Mode Dragging Follower -->
    <PbItem
      v-if="currentModeIsDragged"
      class="pb-mode-item-drag-follower"
      :style="dragFollowerStyle"
    >
      {{ mode.shortName }}
      <PbItemDetails>
        {{ itemDetails }}
      </PbItemDetails>
    </PbItem>
    <!-- End Mode Dragging Follower -->
    <DeleteModeConfirmDialog
      v-model="deleteModeConfirm"
      :mode="mode"
      :options="{
        query: true,
      }"
    />
  </li>
</template>

<script setup>
import {
  computed,
  ref,
  toRef,
  watch,
} from 'vue'
import useRootStore from '@/stores/root.store'
import useModeStore from '@/stores/mode.store'
import { useDrag, useDrop } from '@/composables/useDragDrop'
import { useReadonly } from '@/composables/useProfile'
import useIsGlobal from '@/composables/useIsGlobal'
import useModeDetails from '@/composables/useModeDetails'
import LinkBox from '@/components/LinkBox/LinkBox.vue'
import ReportIcon from '@/components/ItemIcons/ReportIcon.vue'
import PbItem from '@/components/PbItem/PbItem.vue'
import PbItemDetails from '@/components/PbItemDetails/PbItemDetails.vue'
import useTranslate from '@/composables/useTranslate'
import DeleteModeConfirmDialog from '@/components/DeleteModeConfirmDialog/DeleteModeConfirmDialog.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'

const { isGlobal } = useIsGlobal()

const { translate } = useTranslate()

const rootStore = useRootStore()

const reorderGrabber = ref(null)
const topDropzone = ref(null)
const bottomDropzone = ref(null)

const emit = defineEmits([
  'navigate',
])

const props = defineProps({
  mode: { type: Object, default: () => ({}) },
  dragDropContext: { type: Object, default: () => ({}) },
  href: { type: String, default: '' },
  active: { type: Boolean, default: false },
  deletable: { type: Boolean, default: false },
})

const modeStore = useModeStore()

const mode = toRef(props, 'mode')
const modeData = computed(() => mode.value.data)
const readonly = useReadonly()

const index = computed(() => (
  modeData.value._order + 1
))

const duplicateModeTooltip = computed(() => `${translate('pb2.manageModesView.duplicateModeTooltip')} "${mode.value.linkName}"`)

const deleteModeTooltip = computed(() => `${translate('pb2.manageModesView.deleteModeTooltip')} "${mode.value.linkName}"`)

const anyModeIsDragged = computed(() => props.dragDropContext.isDragging.value)

const dragEnabled = computed(() => !readonly.value)

const { isDragging: currentModeIsDragged, dragX, dragY } = useDrag(
  reorderGrabber,
  props.dragDropContext,
  mode,
  dragEnabled,
)

watch(currentModeIsDragged, () => {
  // Add the "grabbed" cursor on everything if we're dragging
  rootStore.rootClasses['pb-dragging-active'] = currentModeIsDragged
})

const dragFollowerStyle = computed(() => (
  { transform: `translateX(${dragX.value}px) translateY(-50%) translateY(${dragY.value}px)` }
))

const { active: topDropzoneActive } = useDrop(
  topDropzone,
  props.dragDropContext,
  {
    before: true,
    mode,
  },
)

const { active: bottomDropzoneActive } = useDrop(
  bottomDropzone,
  props.dragDropContext,
  {
    after: true,
    mode,
  },
)

const itemDetails = useModeDetails(mode)

const deleteModeConfirm = ref(false)
function openConfirm() {
  deleteModeConfirm.value = true
}

function duplicate() {
  return modeStore.duplicate({
    _uid: props.mode.data._uid,
    query: true,
    redirect: !isGlobal.value,
  })
}
</script>

<style lang="scss" src="./ModeItem.scss" />
